import { MutableRefObject, ReactElement, useEffect, useRef } from 'react';
import { Grid, Stack } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { AwsClientOverrides, getSchema } from '@localstack/services';
import { DEFAULT_IAM_ROUTES, DEFAULT_KMS_ROUTES, DEFAULT_S3_ROUTES } from '@localstack/constants';
import { LambdaWebFunctionCreateRequest, LambdaWebFunctionCreateOptions, ResolvedMagicSchema } from '@localstack/types';

import {
  ControlledCheckbox,
  ControlledDropzone,
  ControlledRadioGroup,
  MagicForm,
  MemberTitle,
  RelatedResourcePicker,
} from '@localstack/ui';

import { FormFieldset } from '../styled';

import { TemplatePreview } from './TemplatePreview';

const STORAGE_BUCKET_MEMBER: ResolvedMagicSchema = {
  type: 'string',
  name: 'storageBucket',
  documentation:
    'Where the deployment package is uploaded. Leave empty to create a bucket for this function. ' +
    'An existing bucket must have versioning enabled.',
};

interface CreationOptionsProps {
  optionsReader: MutableRefObject<() => LambdaWebFunctionCreateOptions>;
  clientOverrides?: AwsClientOverrides;
}

const CreationOptions = ({ optionsReader, clientOverrides }: CreationOptionsProps): ReactElement => {
  const { control, getValues } = useFormContext<{ creationOptions: LambdaWebFunctionCreateOptions }>();
  const options = useWatch({ control, name: 'creationOptions' });

  // Use the same form for validation/reset, but do not add UI-only shapes to the AWS model.
  useEffect(() => {
    optionsReader.current = () => getValues('creationOptions');
  }, [getValues, optionsReader]);

  return (
    <Stack spacing={2}>
      <ControlledRadioGroup
        control={control}
        name="creationOptions.codeSource"
        label="Code Source"
        aria-label="Code source"
        row
        options={[
          { value: 'template', label: 'Hello World template' },
          { value: 'upload', label: 'Upload ZIP file' },
          { value: 's3', label: 'Amazon S3' },
        ]}
      />
      {options.codeSource === 'template' && <TemplatePreview />}
      {options.codeSource === 'upload' && (
        <ControlledDropzone
          control={control}
          name="creationOptions.zipFile"
          accept=".zip,application/zip"
          shouldUnregister
          rules={{
            required: 'Select a ZIP file',
            validate: (file: File) => file?.name?.toLowerCase().endsWith('.zip') || 'Select a ZIP file',
          }}
        />
      )}
      {options.codeSource !== 's3' && (
        <Grid container spacing={2} alignItems="center">
          <Grid item md={6} sm={12}>
            <MemberTitle member={STORAGE_BUCKET_MEMBER} />
          </Grid>
          <Grid item md={6} sm={12}>
            <RelatedResourcePicker
              control={control}
              fieldName="creationOptions.storageBucket"
              client="S3"
              method="listBuckets"
              arrayKeyName="Buckets"
              property="Name"
              entityName="S3 Bucket"
              creationRoute={DEFAULT_S3_ROUTES.RESOURCES_S3_BUCKET_CREATE}
              clientOverrides={clientOverrides}
            />
          </Grid>
        </Grid>
      )}
    </Stack>
  );
};

const OptionToggles = ({ toggles }: { toggles: [string, string][] }): ReactElement => {
  const { control } = useFormContext<{ creationOptions: LambdaWebFunctionCreateOptions }>();

  return (
    <Grid item xs={12}>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        {toggles.map(([name, label]) => (
          <ControlledCheckbox key={name} control={control} name={`creationOptions.${name}`} label={label} />
        ))}
      </Stack>
    </Grid>
  );
};

export interface LambdaWebFunctionCreateFormProps {
  formId: string;
  loading?: boolean;
  clientOverrides?: AwsClientOverrides;
  onSubmit: (data: LambdaWebFunctionCreateRequest, options: LambdaWebFunctionCreateOptions) => unknown;
}

export const LambdaWebFunctionCreateForm = ({
  formId,
  loading,
  clientOverrides,
  onSubmit,
}: LambdaWebFunctionCreateFormProps): ReactElement => {
  // The API accepts S3 code, not a local ZIP. Keep upload/provisioning choices outside its schema.
  const optionsReader = useRef<() => LambdaWebFunctionCreateOptions>(() => ({ codeSource: 'template' }));
  return (
    <FormFieldset disabled={loading}>
      <MagicForm
        schema={getSchema('LambdaWeb')}
        entry="CreateWebFunctionRequest"
        formId={formId}
        loading={loading}
        onSubmit={(data: LambdaWebFunctionCreateRequest) => onSubmit(data, optionsReader.current())}
        defaultValues={{
          creationOptions: { codeSource: 'template' },
          revisionConfig: { buildConfig: { runtimeConfig: { runtime: 'nodejs24.x' } } },
          endpointConfig: { autoDeploymentMode: 'LatestRevision' },
        }}
        fieldConditions={{
          '^revisionConfig\\.description$': false,
          '^revisionConfig\\.buildConfig\\.codeConfig$': ['$creationOptions.codeSource', '===', 's3'],
          '^endpointConfig\\.regions$': ['$endpointConfig.endpointType', 'in', ['MultiRegion', 'PerRegion']],
          '^endpointConfig\\.(scalingConfig|throttleConfig)$': false,
          '^revisionConfig\\.serviceConfig\\.executionRoleArn$': ['$creationOptions.useExistingRole', '===', true],
          '^revisionConfig\\.kmsKeyArn$': ['$creationOptions.useKmsKey', '===', true],
          '^revisionConfig\\.serviceConfig\\.maxConcurrencyPerEnvironment$': [
            '$creationOptions.configureConcurrency',
            '===',
            true,
          ],
          '^revisionConfig\\.serviceConfig\\.(timeoutSeconds|telemetryConfig)$': false,
        }}
        injectedFields={{
          // Placed inside the structures holding the fields they reveal, so each toggle stays put
          // while its field appears below it. The KMS key is a member of revisionConfig itself.
          '^revisionConfig$': {
            position: 'in',
            element: (
              <>
                <Grid item xs={12}>
                  <CreationOptions optionsReader={optionsReader} clientOverrides={clientOverrides} />
                </Grid>
                <OptionToggles toggles={[['useKmsKey', 'Use KMS key']]} />
              </>
            ),
          },
          '^revisionConfig\\.serviceConfig$': {
            position: 'in',
            element: (
              <OptionToggles
                toggles={[
                  ['useExistingRole', 'Use existing role'],
                  ['configureConcurrency', 'Configure concurrency'],
                ]}
              />
            ),
          },
        }}
        externalFields={{
          '^revisionConfig\\.buildConfig\\.codeConfig\\.s3Object\\.bucket$': (control, fieldName, required) => (
            <RelatedResourcePicker
              control={control}
              fieldName={fieldName}
              client="S3"
              method="listBuckets"
              arrayKeyName="Buckets"
              property="Name"
              entityName="S3 Bucket"
              creationRoute={DEFAULT_S3_ROUTES.RESOURCES_S3_BUCKET_CREATE}
              clientOverrides={clientOverrides}
              required={required}
            />
          ),
          '^revisionConfig\\.serviceConfig\\.executionRoleArn$': (control, fieldName, required) => (
            <RelatedResourcePicker
              control={control}
              fieldName={fieldName}
              client="IAM"
              method="listRoles"
              arrayKeyName="Roles"
              property="Arn"
              entityName="IAM Role"
              creationRoute={DEFAULT_IAM_ROUTES.RESOURCES_IAM_ROLE_CREATE}
              clientOverrides={clientOverrides}
              required={required}
            />
          ),
          '^revisionConfig\\.kmsKeyArn$': (control, fieldName, required) => (
            <RelatedResourcePicker
              control={control}
              fieldName={fieldName}
              client="KMS"
              method="listKeys"
              arrayKeyName="Keys"
              property="KeyArn"
              entityName="KMS Key"
              creationRoute={DEFAULT_KMS_ROUTES.RESOURCES_KMS_KEY_CREATE}
              clientOverrides={clientOverrides}
              required={required}
            />
          ),
        }}
      />
    </FormFieldset>
  );
};
